import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useAuth, useLocale, useSettings } from '../context';
import { useIsMobile } from '../hooks/useIsMobile';
import { pushRoute } from '../navigation';
import BalanceSum from './BalanceSum';
import { IconAccountCircleFilled, IconLogOut } from './VurgunIcons';

const AccountMenu = forwardRef(function AccountMenu({
  balance,
  inGame = false,
  onNavigate,
  onLogout,
  iconOnly = false,
  unreadNotifications = 0,
  onUnreadChange,
  onSheetChange,
}, ref) {
  const { user } = useAuth();
  const _locale = useLocale();
  const tr = _locale.t;
  const { settings } = useSettings();
  const isMobile = useIsMobile();
  const useSheet = iconOnly && isMobile;
  const [open, setOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetSection, setSheetSection] = useState(undefined);
  const rootRef = useRef(null);
  const closeTimer = useRef(null);

  useImperativeHandle(ref, () => ({
    openSection(section) {
      if (!useSheet) {
        onNavigate?.('account', { accountSection: section || 'cashier' });
        pushRoute('account', { accountSection: section || 'cashier' });
        return;
      }
      setSheetSection(section || undefined);
      setSheetOpen(true);
    },
    closeSheet() {
      setSheetOpen(false);
      setSheetSection(undefined);
    },
  }), [useSheet, onNavigate]);

  useEffect(() => {
    onSheetChange?.({ open: useSheet && sheetOpen, section: sheetSection });
  }, [useSheet, sheetOpen, sheetSection, onSheetChange]);

  useEffect(() => {
    if (useSheet) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [useSheet]);

  const clearCloseTimer = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const handleEnter = () => {
    clearCloseTimer();
    setOpen(true);
  };

  const handleLeave = () => {
    clearCloseTimer();
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };

  const go = (section) => {
    setOpen(false);
    onNavigate?.('account', { accountSection: section });
    pushRoute('account', { accountSection: section });
  };

  const goVip = () => {
    setOpen(false);
    onNavigate?.('vip');
    pushRoute('vip');
  };

  const closeSheet = () => {
    setSheetOpen(false);
    setSheetSection(undefined);
  };

  if (!user) return null;

  return (
    <div
      className={`header-account-menu${iconOnly ? ' header-account-menu--icon-only' : ''}`}
      ref={rootRef}
      onMouseEnter={iconOnly ? undefined : handleEnter}
      onMouseLeave={iconOnly ? undefined : handleLeave}
    >
      <button
        type="button"
        className={`header-account-menu__trigger${open ? ' is-open' : ''}${unreadNotifications > 0 ? ' has-notifications' : ''}`}
        aria-label={tr('header.account')}
        aria-expanded={useSheet ? sheetOpen : open}
        onClick={() => {
          if (useSheet) {
            setSheetSection(undefined);
            setSheetOpen(true);
          } else {
            go('profile');
          }
        }}
      >
        <span className="header-account-menu__icon" aria-hidden="true">
          <IconAccountCircleFilled />
        </span>
        {unreadNotifications > 0 && (
          <span className="header-account-menu__notify-dot" aria-hidden="true" />
        )}
        {!iconOnly && (
          inGame ? (
            <span className="header-account-menu__sum header-account-menu__sum--ingame">
              {tr('header.inGame') || '(Oyunda)'}
            </span>
          ) : (
            <BalanceSum
              amount={balance}
              hidden={settings.hideBalance}
              className={`header-account-menu__sum${settings.hideBalance ? ' is-hidden' : ''}`}
            />
          )
        )}
      </button>
      {/* Mobile sheet is owned by App (single instance). Avatar toggles via onSheetChange. */}
      {!useSheet && open && (
        <div className="header-account-menu__dropdown">
          <button type="button" className="header-account-menu__item" onClick={() => go('profile')}>
            {tr('header.account')}
          </button>
          <button type="button" className="header-account-menu__item" onClick={() => go('cashier')}>
            {tr('account.menu.deposit')}
          </button>
          <button type="button" className="header-account-menu__item" onClick={() => go('support')}>
            {tr('account.menu.requests')}
          </button>
          <button type="button" className="header-account-menu__item" onClick={goVip}>
            {tr('account.menu.vip')}
          </button>
          <button type="button" className="header-account-menu__item header-account-menu__item--logout" onClick={onLogout}>
            <span className="header-account-menu__logout-icon" aria-hidden="true">
              <IconLogOut />
            </span>
            {tr('header.logout')}
          </button>
        </div>
      )}
    </div>
  );
});

export default AccountMenu;
